import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  classKey,
  getAttendanceForStudent,
  getAttendanceRecords,
  getSessionUser,
  getStudentRecordForSession,
  getStudentsForClass,
  saveAttendanceRecord,
} from "@/lib/eisStore";

function buildDateKey(date) {
  return date.toISOString().slice(0, 10);
}

function StudentAttendance({ user }) {
  const studentRecord = getStudentRecordForSession(user) || user;
  const studentClassKey =
    studentRecord?.standard && studentRecord?.division
      ? classKey(studentRecord.standard, studentRecord.division)
      : user?.standard && user?.division
        ? classKey(user.standard, user.division)
        : "";
  const studentId = studentRecord?.id || user.id || user.email || user.name;
  const attendance = studentClassKey ? getAttendanceForStudent(studentClassKey, studentId) : [];
  const absentRecords = attendance.filter((item) => item.status === "absent");
  const absentDates = absentRecords.map((item) => new Date(item.date));

  const [month, setMonth] = useState(new Date());
  const monthlyRecords = studentClassKey
    ? Object.entries(getAttendanceRecords(studentClassKey)).filter(([date]) => {
        const current = new Date(date);
        return current.getMonth() === month.getMonth() && current.getFullYear() === month.getFullYear();
      })
    : [];
  const absentInMonth = monthlyRecords.filter(([, record]) => (record[studentId] || "present") === "absent").length;
  const presentInMonth = Math.max(monthlyRecords.length - absentInMonth, 0);

  return (
    <AppShell>
      <div data-testid="attendance-page" className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
              Attendance
            </h2>
            <p className="text-slate-500 mt-1 text-sm">
              Your class attendance history.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="px-4 py-2 rounded-xl bg-green-50 text-green-700 font-semibold">
              Present: {presentInMonth}
            </div>
            <div className="px-4 py-2 rounded-xl bg-red-50 text-red-700 font-semibold">
              Absent: {absentInMonth}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
          <Card>
            <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>Monthly View</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                data-testid="attendance-calendar"
                month={month}
                onMonthChange={setMonth}
                modifiers={{ absent: absentDates }}
                modifiersClassNames={{
                  absent:
                    "bg-red-500 text-white hover:bg-red-500 hover:text-white focus:bg-red-600 rounded-full",
                }}
                className="rounded-md border w-full max-w-md"
                classNames={{
                  day: "h-10 w-10 p-0 font-medium hover:bg-slate-100 rounded-full",
                  head_cell:
                    "text-slate-500 rounded-md w-10 font-semibold text-xs uppercase",
                  cell: "relative p-0 text-center text-sm w-10 h-10",
                  caption_label: "text-base font-semibold",
                  nav_button: "h-8 w-8 bg-transparent p-0 hover:opacity-100",
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Absent Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {absentRecords.length ? (
                <div className="space-y-2">
                  {absentRecords
                    .slice()
                    .reverse()
                    .map((item) => (
                      <div key={item.date} className="rounded-xl border border-slate-200 px-4 py-3">
                        <p className="font-semibold text-slate-800">
                          {new Date(item.date).toLocaleDateString("en-IN", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                        <p className="text-xs text-red-600 font-medium mt-1">Marked absent</p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  No absence records found for your account.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function TeacherAttendance({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [date, setDate] = useState(buildDateKey(new Date()));
  const [records, setRecords] = useState({});
  const students = teacherClassKey ? getStudentsForClass(teacherClassKey) : [];

  useEffect(() => {
    if (!teacherClassKey) {
      setRecords({});
      return;
    }
    const existing = getAttendanceRecords(teacherClassKey)[date] || {};
    const next = {};
    getStudentsForClass(teacherClassKey).forEach((student) => {
      next[student.id] = existing[student.id] || "present";
    });
    setRecords(next);
  }, [teacherClassKey, date]);

  const summary = useMemo(() => {
    const present = Object.values(records).filter((value) => value === "present").length;
    const absent = Object.values(records).filter((value) => value === "absent").length;
    return { present, absent };
  }, [records]);

  const updateAttendance = (studentId, status) => {
    setRecords((current) => ({ ...current, [studentId]: status }));
  };

  const save = () => {
    if (!teacherClassKey) return;
    saveAttendanceRecord(teacherClassKey, date, records);
  };

  if (!user?.assignedClassKey) {
    return (
      <AppShell>
        <div data-testid="attendance-page" className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Attendance
          </h2>
          <Card>
            <CardContent className="p-6">
              <p className="text-slate-700">
                You are not assigned as a class teacher, so attendance is not available for your account.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div data-testid="attendance-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Attendance
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Mark attendance for your class.
          </p>
        </div>

        <TeacherClassPicker
          teacher={user}
          onChange={(value) => setTeacherClassKey(value)}
        />

        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Date</Label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <DetailStat label="Present" value={summary.present} tone="green" />
              <DetailStat label="Absent" value={summary.absent} tone="red" />
            </div>

            {!teacherClassKey ? (
              <p className="text-sm text-slate-500">
                Select a class to load the roster.
              </p>
            ) : (
              <>
                <div className="space-y-3">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4"
                    >
                      <div>
                        <p className="font-semibold text-slate-800">{student.name}</p>
                        <p className="text-xs text-slate-500">
                          Roll {student.rollNo} · {student.standard} {student.division}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={records[student.id] === "present" ? "default" : "outline"}
                          className={records[student.id] === "present" ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                          onClick={() => updateAttendance(student.id, "present")}
                        >
                          Present
                        </Button>
                        <Button
                          type="button"
                          variant={records[student.id] === "absent" ? "default" : "outline"}
                          className={records[student.id] === "absent" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                          onClick={() => updateAttendance(student.id, "absent")}
                        >
                          Absent
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  onClick={save}
                  className="rounded-full text-white"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Save Attendance
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value}</span>
    </div>
  );
}

function DetailStat({ label, value, tone }) {
  const tones = {
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

export default function Attendance() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherAttendance user={user} />;
  }
  return <StudentAttendance user={user || {}} />;
}
